// Local-first cache for workflow state.
//
// Each open .flw is cached so reopening a flow is instant (no spinner, no
// network round-trip). The cache also acts as a draft buffer: edits are
// written here on every change but NOT pushed to the backend until the user
// clicks the Save button. The dirty index lets the Save button find every
// flow that needs flushing in a single pass.
//
// Storage layout:
// - Flow states are BIG (retained variables, node shapes), so they live in
//   IndexedDB (flowDraftDb) with an in-memory mirror. Reads stay synchronous
//   against the mirror; IndexedDB writes are fire-and-forget persistence.
//   Legacy localStorage entries are migrated to IndexedDB (and removed) once
//   at startup — they used to fill the ~5MB origin quota on their own.
// - The dirty index and pending workbook ops are tiny and stay in
//   localStorage, written through storageQuota so a full quota evicts
//   disposable data instead of dropping the write.
//
// whenReady() resolves once the mirror is loaded; the flow-open path awaits
// it so a fast reload can't miss a draft that is still loading from disk.

var flowDraftDb = require('./flowDraftDb');
var storageQuota = require('./storageQuota');

var KEY_PREFIX = 'aflow:flow:';
var DIRTY_INDEX_KEY = 'aflow:flow:dirty';
var WORKBOOK_OPS_KEY = 'aflow:workbook:ops';

// flowId → { state, dirty, updatedAt, baseUpdatedAt }
var memoryCache = {};
var readyPromise = null;

function hasStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch (e) {
    return false;
  }
}

// Move legacy localStorage flow entries into the mirror + IndexedDB, keeping
// whichever copy is newer, then delete them to reclaim localStorage quota.
function migrateLegacyLocalStorage() {
  if (!hasStorage()) return;
  var legacyKeys = [];
  for (var i = 0; i < window.localStorage.length; i += 1) {
    var key = window.localStorage.key(i);
    if (key && key.indexOf(KEY_PREFIX) === 0 && key !== DIRTY_INDEX_KEY) legacyKeys.push(key);
  }
  legacyKeys.forEach(function(key) {
    var flowId = key.slice(KEY_PREFIX.length);
    var entry = null;
    try { entry = JSON.parse(window.localStorage.getItem(key)); } catch (e) {}
    if (entry && typeof entry === 'object' && flowId) {
      var existing = memoryCache[flowId];
      if (!existing || (entry.updatedAt || 0) > (existing.updatedAt || 0)) {
        memoryCache[flowId] = entry;
        flowDraftDb.putEntry(flowId, entry);
      }
    }
    try { window.localStorage.removeItem(key); } catch (e) {}
  });
  if (legacyKeys.length) {
    console.log('[localFlowCache] Migrated ' + legacyKeys.length + ' flow cache(s) from localStorage to IndexedDB');
  }
}

function init() {
  if (readyPromise) return readyPromise;
  readyPromise = flowDraftDb.getAllEntries()
    .then(function(rows) {
      rows.forEach(function(row) {
        if (!row || !row.flowId) return;
        memoryCache[String(row.flowId)] = {
          state: row.state || {},
          dirty: !!row.dirty,
          updatedAt: row.updatedAt || 0,
          baseUpdatedAt: row.baseUpdatedAt || null
        };
      });
      migrateLegacyLocalStorage();
    })
    .catch(function() {});
  return readyPromise;
}

// Kick the load immediately so the mirror is usually ready before the first
// flow opens; whenReady() is the hard guarantee for early readers.
if (typeof window !== 'undefined') init();

function whenReady() {
  return init();
}

function readFlowCache(flowId) {
  if (!flowId) return null;
  return memoryCache[String(flowId)] || null;
}

function persistEntry(flowId, entry) {
  memoryCache[String(flowId)] = entry;
  flowDraftDb.putEntry(flowId, entry);
}

function writeFlowCache(flowId, state, options) {
  if (!flowId) return;
  var dirty = !!(options && options.dirty);
  // baseUpdatedAt = the server's updatedAt this draft is based on. Callers that
  // just refreshed from the server pass it explicitly; edit-path callers omit
  // it and the previous baseline is carried forward, so the draft always
  // remembers which server version it forked from (conflict detection).
  var prev = readFlowCache(flowId);
  var baseUpdatedAt = options && options.baseUpdatedAt !== undefined
    ? options.baseUpdatedAt
    : (prev ? prev.baseUpdatedAt || null : null);
  persistEntry(flowId, {
    state: state || {},
    dirty: dirty,
    updatedAt: Date.now(),
    baseUpdatedAt: baseUpdatedAt || null
  });
  if (dirty) addToDirtyIndex(flowId);
  else removeFromDirtyIndex(flowId);
}

function markFlowClean(flowId, baseUpdatedAt) {
  if (!flowId) return;
  var entry = readFlowCache(flowId);
  if (entry) {
    entry.dirty = false;
    if (baseUpdatedAt) entry.baseUpdatedAt = baseUpdatedAt;
    persistEntry(flowId, entry);
  }
  removeFromDirtyIndex(flowId);
}

// Adopt a new server baseline without touching the draft state or dirty flag.
// Used when the user chooses "keep my draft" after a conflict — their next
// save should win over the version they reviewed and rejected.
function setFlowBaseline(flowId, baseUpdatedAt) {
  if (!flowId) return;
  var entry = readFlowCache(flowId);
  if (!entry) return;
  entry.baseUpdatedAt = baseUpdatedAt || null;
  persistEntry(flowId, entry);
}

function clearFlowCache(flowId) {
  if (!flowId) return;
  delete memoryCache[String(flowId)];
  flowDraftDb.deleteEntry(flowId);
  storageQuota.safeRemoveItem(KEY_PREFIX + String(flowId)); // legacy leftover
  removeFromDirtyIndex(flowId);
}

function readDirtyIndex() {
  if (!hasStorage()) return {};
  try {
    var raw = window.localStorage.getItem(DIRTY_INDEX_KEY);
    if (!raw) return {};
    var parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

function writeDirtyIndex(idx) {
  storageQuota.safeSetItem(DIRTY_INDEX_KEY, JSON.stringify(idx || {}));
}

function addToDirtyIndex(flowId) {
  var idx = readDirtyIndex();
  idx[String(flowId)] = true;
  writeDirtyIndex(idx);
}

function removeFromDirtyIndex(flowId) {
  var idx = readDirtyIndex();
  if (Object.prototype.hasOwnProperty.call(idx, String(flowId))) {
    delete idx[String(flowId)];
    writeDirtyIndex(idx);
  }
}

function listDirtyFlowIds() {
  return Object.keys(readDirtyIndex());
}

// Pending workbook operations (rename, move, etc.) — deferred until Save click.
function readPendingWorkbookOps() {
  if (!hasStorage()) return [];
  try {
    var raw = window.localStorage.getItem(WORKBOOK_OPS_KEY);
    if (!raw) return [];
    var parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function writePendingWorkbookOps(ops) {
  storageQuota.safeSetItem(WORKBOOK_OPS_KEY, JSON.stringify(ops || []));
}

function clearPendingWorkbookOps() {
  storageQuota.safeRemoveItem(WORKBOOK_OPS_KEY);
}

module.exports = {
  whenReady: whenReady,
  readFlowCache: readFlowCache,
  writeFlowCache: writeFlowCache,
  markFlowClean: markFlowClean,
  setFlowBaseline: setFlowBaseline,
  clearFlowCache: clearFlowCache,
  listDirtyFlowIds: listDirtyFlowIds,
  readPendingWorkbookOps: readPendingWorkbookOps,
  writePendingWorkbookOps: writePendingWorkbookOps,
  clearPendingWorkbookOps: clearPendingWorkbookOps
};
